import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import belowIcon from '../../asset/icon/below.png';
import deleteIcon from '../../asset/icon/delete.png';
import defaultImg from '../../asset/project/default_1.png';
import {
  BlackFullWidthBtn,
  PaperBlurFitBtn,
} from '../../shared/component/headless/button/BtnBody';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { ChangeModalFrame } from '../../shared/component/modal/ChangeModalFrame';
import PageSizeStation from '../../shared/component/PageSizeStation';
import SelectBox from '../../shared/component/SelectBox';
import { getBtnName } from '../../shared/constants/btnNames';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { LIMIT_PAGE_NUM } from '../../shared/constants/limits';
import { getPageMessage } from '../../shared/constants/pageMessages';
import {
  WorkImportCandidateData,
  WorkImportCandidatePageData,
  WorkImportSourceData,
  WorkTagData,
} from '../../shared/dto/EntityRepository';
import {
  AlertPosition,
  AlertType,
  MemberType,
  PerPage,
  WorkImportSourceAvailability,
} from '../../shared/enum/EnumRepository';
import { useApprovalRequestActions } from '../../shared/hooks/useApi/useApprovalRequest';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import { useScrollContainerMarqueeSelect } from '../../shared/hooks/useScrollContainerMarqueeSelect';
import { getWorkImportCandidates, getWorkImportSources } from '../../shared/api/workImportApi';
import { getWorkTagList } from '../../shared/api/workTagApi';
import { useAuthStore } from '../../shared/store/authStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useSettingStore } from '../../shared/store/settingStore';
import { useWorkContentModeStore } from '../../shared/store/workContentModeStore';
import { CurrPageButton, PageButton, PageWindowButton } from './PageAboutWork';
import { getCandidateSize } from './workImportCandidateSize';
import { getCandidateMaterial, getCandidateTitle } from './workImportCandidateText';

type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

interface WorkCopyImportModalProps {
  isMobile?: boolean;
}

interface CandidateThumbnailProps {
  originUrl: string;
  alt: string;
}

const CandidateThumbnail: React.FC<CandidateThumbnailProps> = ({ originUrl, alt }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useOptimizedImageUrl(originUrl, containerRef);
  return (
    <ThumbnailFrame ref={containerRef}>
      <ThumbnailImage src={imageUrl || defaultImg} alt={alt} />
    </ThumbnailFrame>
  );
};

interface SelectedWorkThumbnailProps {
  candidate: WorkImportCandidateData;
  layout: 'strip' | 'stack';
  onRemove: (candidateId: number) => void;
}

const SelectedWorkThumbnail: React.FC<SelectedWorkThumbnailProps> = ({
  candidate,
  layout,
  onRemove,
}) => {
  const [isHover, setIsHover] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useOptimizedImageUrl(candidate.uploadFile?.originUrl ?? '', containerRef);
  const title = candidate.title?.trim()
    || candidate.titleEn?.trim()
    || getPageMessage('WORK_COPY_IMPORT_UNTITLED');

  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation();
    onRemove(candidate.id);
  };

  return (
    <SelectedThumbWrapper
      $layout={layout}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onFocus={() => setIsHover(true)}
      onBlur={() => setIsHover(false)}
    >
      <SelectedImageBox ref={containerRef} $layout={layout}>
        <SelectedThumbImage
          $layout={layout}
          $isHover={isHover}
          src={imageUrl || defaultImg}
          alt={title}
        />
        <SelectedRemoveButton
          size="fit"
          border={false}
          isTransparent
          backgroundImg={deleteIcon}
          aria-label={`${title} 선택 취소`}
          $layout={layout}
          $isHover={isHover}
          onClick={handleRemove}
        />
      </SelectedImageBox>
    </SelectedThumbWrapper>
  );
};

const getSourceLabel = (source: WorkImportSourceData): string => {
  const groupMemberName = source.groupMemberName?.trim();
  const username = source.username?.trim();
  const handle = source.handle?.trim();
  const accountName = username || (handle ? `@${handle}` : source.aui);
  return groupMemberName && groupMemberName !== accountName
    ? `${groupMemberName} · ${accountName}${handle && username ? ` (@${handle})` : ''}`
    : `${accountName}${handle && username ? ` (@${handle})` : ''}`;
};

const getCandidateId = (candidate: WorkImportCandidateData): string => String(candidate.id);

const getSourceAvailabilityMessage = (
  availability: WorkImportSourceAvailability,
): string => {
  switch (availability) {
    case WorkImportSourceAvailability.GROUP_MEMBER_INACTIVE:
      return getPageMessage('WORK_COPY_IMPORT_GROUP_MEMBER_INACTIVE');
    case WorkImportSourceAvailability.ACCOUNT_INACTIVE:
      return getPageMessage('WORK_COPY_IMPORT_ACCOUNT_INACTIVE');
    case WorkImportSourceAvailability.ACCOUNT_PRIVATE:
      return getPageMessage('WORK_COPY_IMPORT_ACCOUNT_PRIVATE');
    case WorkImportSourceAvailability.WORKS_PRIVATE:
      return getPageMessage('WORK_COPY_IMPORT_WORKS_PRIVATE');
    case WorkImportSourceAvailability.ACCOUNT_NOT_ARTIST:
      return getPageMessage('WORK_COPY_IMPORT_ACCOUNT_NOT_ARTIST');
    default:
      return getPageMessage('WORK_COPY_IMPORT_SOURCE_UNAVAILABLE');
  }
};

const WorkCopyImportModal: React.FC<WorkCopyImportModalProps> = ({
  isMobile = false,
}) => {
  const accountAui = useAuthStore((state) => state.user?.aui);
  const memberType = useSettingStore((state) => state.setting?.memberType);
  const workContentMode = useWorkContentModeStore((state) => state.workContentMode);
  const { isClosing, clearModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { createWorkCopyApprovalRequest } = useApprovalRequestActions();

  const [sources, setSources] = useState<WorkImportSourceData[]>([]);
  const [sourceStatus, setSourceStatus] = useState<LoadStatus>('idle');
  const [sourceReloadKey, setSourceReloadKey] = useState(0);
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [candidatePage, setCandidatePage] = useState<WorkImportCandidatePageData | null>(null);
  const [candidateStatus, setCandidateStatus] = useState<LoadStatus>('idle');
  const [candidateReloadKey, setCandidateReloadKey] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PerPage>(PerPage.PAGE_10);
  const [tags, setTags] = useState<WorkTagData[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(new Set());
  const [selectedWorks, setSelectedWorks] = useState<Map<number, WorkImportCandidateData>>(
    new Map(),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const candidateListRef = useRef<HTMLDivElement>(null);

  const close = useCallback(
    () => clearModal(isMobile ? 400 : 300),
    [clearModal, isMobile],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [close]);

  useEffect(() => {
    if (!accountAui || memberType !== MemberType.GROUP) return;
    let active = true;
    setSourceStatus('loading');
    setSelectedWorks(new Map());
    setTags([]);
    setSelectedTagIds(new Set());
    setCandidatePage(null);
    setPage(1);

    void getWorkImportSources(accountAui)
      .then((response) => {
        if (!active) return;
        const nextSources = response.data;
        setSources(nextSources);
        setSelectedSourceId((current) =>
          nextSources.some((source) => source.groupMemberId === current)
            ? current
            : nextSources[0]?.groupMemberId ?? null);
        setSourceStatus('success');
      })
      .catch(() => {
        if (!active) return;
        setSources([]);
        setSelectedSourceId(null);
        setSourceStatus('error');
      });

    return () => {
      active = false;
    };
  }, [accountAui, memberType, sourceReloadKey]);

  const selectedSource = sources.find(
    (source) => source.groupMemberId === selectedSourceId,
  );

  const selectedTagIdList = useMemo(
    () => [...selectedTagIds].sort((left, right) => left - right),
    [selectedTagIds],
  );

  useEffect(() => {
    setTags([]);
    if (
      !selectedSource
      || selectedSource.availability !== WorkImportSourceAvailability.AVAILABLE
    ) return;

    let active = true;
    void getWorkTagList(selectedSource.aui)
      .then((response) => {
        if (active) setTags(response.data);
      })
      .catch(() => {
        if (active) setTags([]);
      });

    return () => {
      active = false;
    };
  }, [selectedSource]);

  useEffect(() => {
    if (
      !accountAui
      || selectedSourceId === null
      || selectedSource?.availability !== WorkImportSourceAvailability.AVAILABLE
    ) {
      setCandidatePage(null);
      setCandidateStatus('idle');
      return;
    }

    let active = true;
    setCandidateStatus('loading');
    setCandidatePage(null);
    void getWorkImportCandidates(
      accountAui,
      selectedSourceId,
      page,
      Number(pageSize),
      selectedTagIdList,
    )
      .then((response) => {
        if (!active) return;
        if (response.data.totalPages > 0 && page > response.data.totalPages) {
          setPage(response.data.totalPages);
          return;
        }
        setCandidatePage(response.data);
        setSelectedWorks((current) => {
          let changed = false;
          const next = new Map(current);
          response.data.content.forEach((candidate) => {
            if (candidate.pendingApproval && next.delete(candidate.id)) {
              changed = true;
            } else if (next.has(candidate.id)) {
              next.set(candidate.id, candidate);
              changed = true;
            }
          });
          return changed ? next : current;
        });
        setCandidateStatus('success');
      })
      .catch(() => {
        if (!active) return;
        setCandidateStatus('error');
      });

    return () => {
      active = false;
    };
  }, [
    accountAui,
    selectedSourceId,
    selectedSource?.availability,
    page,
    pageSize,
    selectedTagIdList,
    candidateReloadKey,
  ]);

  const sourceById = useMemo(
    () => new Map(sources.map((source) => [source.groupMemberId, source])),
    [sources],
  );

  const getSourceDisplayText = (groupMemberId: number): string => {
    const source = sourceById.get(groupMemberId);
    return source ? getSourceLabel(source) : '';
  };

  const handleSourceChange = (groupMemberId: number) => {
    if (groupMemberId === selectedSourceId) return;

    const changeSource = () => {
      setSelectedSourceId(groupMemberId);
      setSelectedWorks(new Map());
      setTags([]);
      setSelectedTagIds(new Set());
      setCandidatePage(null);
      setPage(1);
    };

    if (selectedWorks.size === 0) {
      changeSource();
      return;
    }

    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_CHANGE_WORK_IMPORT_ACCOUNT'),
      callBack: changeSource,
    });
  };

  const handleCandidateChange = (candidate: WorkImportCandidateData) => {
    if (candidate.pendingApproval) return;
    if (!selectedWorks.has(candidate.id) && selectedWorks.size >= 10) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_IMPORT_MAX_EXCEEDED', { maxCount: 10 }),
      });
      return;
    }
    setSelectedWorks((current) => {
      const next = new Map(current);
      if (next.has(candidate.id)) {
        next.delete(candidate.id);
      } else {
        next.set(candidate.id, candidate);
      }
      return next;
    });
  };

  const handleRemoveSelectedWork = useCallback((candidateId: number) => {
    setSelectedWorks((current) => {
      if (!current.has(candidateId)) return current;
      const next = new Map(current);
      next.delete(candidateId);
      return next;
    });
  }, []);

  const handleTagChange = (tagId: number) => {
    setSelectedTagIds((current) => {
      const next = new Set(current);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
    setPage(1);
  };

  const handlePageSizeChange = (value: PerPage) => {
    setPageSize(value);
    setPage(1);
  };

  const marqueeCandidates = useMemo(
    () => candidatePage?.content.filter((candidate) => !candidate.pendingApproval) ?? [],
    [candidatePage],
  );

  const commitMarquee = useCallback((hitCandidates: WorkImportCandidateData[]) => {
    const newCandidates = hitCandidates.filter(
      (candidate) => !candidate.pendingApproval && !selectedWorks.has(candidate.id),
    );
    const availableCount = Math.max(0, 10 - selectedWorks.size);

    if (newCandidates.length > availableCount) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_IMPORT_MAX_EXCEEDED', { maxCount: 10 }),
      });
    }
    if (availableCount === 0) return;

    setSelectedWorks((current) => {
      const next = new Map(current);
      newCandidates.slice(0, availableCount).forEach((candidate) => {
        next.set(candidate.id, candidate);
      });
      return next;
    });
  }, [enqueueAlert, selectedWorks]);

  const {
    handleContainerMouseDown,
    handleContainerClickCapture,
    marqueeBox,
    marqueeSelecting,
    hitItems,
  } = useScrollContainerMarqueeSelect({
    items: marqueeCandidates,
    getItemId: getCandidateId,
    containerRef: candidateListRef,
    enabled: !isMobile && candidateStatus === 'success' && marqueeCandidates.length > 0,
    onCommit: commitMarquee,
  });

  const marqueeHitIds = useMemo(
    () => new Set(hitItems.map((candidate) => candidate.id)),
    [hitItems],
  );

  const currentPageWindow = Math.ceil(page / LIMIT_PAGE_NUM);
  const totalPageWindows = Math.ceil((candidatePage?.totalPages ?? 0) / LIMIT_PAGE_NUM);
  const startPage = (currentPageWindow - 1) * LIMIT_PAGE_NUM + 1;
  const endPage = Math.min(
    startPage + LIMIT_PAGE_NUM - 1,
    candidatePage?.totalPages ?? 0,
  );

  const handlePageWindowClick = (direction: 'prev' | 'next') => {
    if (candidateStatus === 'loading') return;
    if (direction === 'prev' && currentPageWindow > 1) {
      setPage((currentPageWindow - 2) * LIMIT_PAGE_NUM + 1);
    } else if (direction === 'next' && currentPageWindow < totalPageWindows) {
      setPage(currentPageWindow * LIMIT_PAGE_NUM + 1);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (selectedSourceId === null || selectedWorks.size === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_WORK_COPY_EMPTY'),
      });
      return;
    }
    setIsSubmitting(true);
    const sent = await createWorkCopyApprovalRequest({
      groupMemberId: selectedSourceId,
      workIds: [...selectedWorks.keys()],
    });
    if (sent) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getPageMessage('WORK_COPY_IMPORT_SENT'),
      });
      close();
      return;
    }
    setSelectedWorks(new Map());
    setCandidateReloadKey((current) => current + 1);
    setIsSubmitting(false);
  };

  if (!accountAui || memberType !== MemberType.GROUP) return null;

  const renderCandidates = () => {
    if (
      selectedSource
      && selectedSource.availability !== WorkImportSourceAvailability.AVAILABLE
    ) {
      return (
        <StatusArea>
          {getSourceAvailabilityMessage(selectedSource.availability)}
        </StatusArea>
      );
    }
    if (candidateStatus === 'idle' || candidateStatus === 'loading') {
      return <StatusArea>{getPageMessage('WORK_COPY_IMPORT_WORK_LOADING')}</StatusArea>;
    }
    if (candidateStatus === 'error') {
      return (
        <StatusArea role="alert">
          <span>{getPageMessage('WORK_COPY_IMPORT_WORK_FAILED')}</span>
          <RetryButton onClick={() => setCandidateReloadKey((current) => current + 1)}>
            {getBtnName('RETRY')}
          </RetryButton>
        </StatusArea>
      );
    }
    if (!candidatePage || candidatePage.content.length === 0) {
      return (
        <StatusArea>
          {getPageMessage(
            selectedTagIds.size > 0
              ? 'WORK_COPY_IMPORT_LABEL_EMPTY'
              : 'WORK_COPY_IMPORT_WORK_EMPTY',
          )}
        </StatusArea>
      );
    }

    return (
      <CandidateList
        ref={candidateListRef}
        role="listbox"
        aria-multiselectable="true"
        onMouseDown={handleContainerMouseDown}
        onClickCapture={handleContainerClickCapture}
        $marqueeSelecting={marqueeSelecting}
      >
        {candidatePage.content.map((candidate) => {
          const disabled = candidate.pendingApproval;
          const selected = selectedWorks.has(candidate.id);
          const title = getCandidateTitle(candidate, workContentMode);
          const material = getCandidateMaterial(candidate, workContentMode);
          const size = getCandidateSize(candidate);
          return (
            <CandidateRow
              key={candidate.id}
              data-work-id={candidate.id}
              role="option"
              tabIndex={disabled ? -1 : 0}
              aria-selected={selected}
              aria-disabled={disabled}
              aria-label={disabled
                ? `${title}, ${getPageMessage('WORK_COPY_IMPORT_PENDING')}`
                : undefined}
              title={disabled ? getPageMessage('WORK_COPY_IMPORT_PENDING') : undefined}
              $disabled={disabled}
              $selected={selected}
              $isMarqueeHit={marqueeHitIds.has(candidate.id) && !selected && !disabled}
              onClick={() => handleCandidateChange(candidate)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                handleCandidateChange(candidate);
              }}
            >
              <CandidateImageBlock>
                <CandidateThumbnail
                  originUrl={candidate.uploadFile?.originUrl ?? ''}
                  alt={title}
                />
              </CandidateImageBlock>
              <CandidateTitle title={title}>{title}</CandidateTitle>
              <CandidateYear>{candidate.prodYear ?? '-'}</CandidateYear>
              <CandidateMaterial title={material}>{material}</CandidateMaterial>
              <CandidateSize title={size}>{size}</CandidateSize>
            </CandidateRow>
          );
        })}
        {marqueeBox && (
          <MarqueeSelectionBox
            style={{
              left: marqueeBox.left,
              top: marqueeBox.top,
              width: marqueeBox.width,
              height: marqueeBox.height,
            }}
          />
        )}
      </CandidateList>
    );
  };

  const renderLabels = () => {
    if (tags.length === 0) return null;
    return (
      <LabelContainer data-suppress-marquee>
        {tags.map((tag) => (
          <LabelButton
            key={tag.id}
            type="button"
            $isActive={selectedTagIds.has(tag.id)}
            aria-pressed={selectedTagIds.has(tag.id)}
            onClick={() => handleTagChange(tag.id)}
          >
            {tag.name}
          </LabelButton>
        ))}
      </LabelContainer>
    );
  };

  const renderSelectedWorks = (layout: 'strip' | 'stack') => (
    [...selectedWorks.values()].map((candidate) => (
      <SelectedWorkThumbnail
        key={candidate.id}
        candidate={candidate}
        layout={layout}
        onRemove={handleRemoveSelectedWork}
      />
    ))
  );

  const renderPagination = () => {
    if (!candidatePage || candidateStatus !== 'success') return null;
    return (
      <Pagination aria-label="Work pages">
        <PageButtonContainer>
          {currentPageWindow > 1 && (
            <PageWindowButton onClick={() => handlePageWindowClick('prev')}>
              ←
            </PageWindowButton>
          )}
          {Array.from(
            { length: Math.max(0, endPage - startPage + 1) },
            (_, index) => startPage + index,
          ).map((pageNumber) => (
            pageNumber === page ? (
              <CurrPageButton key={pageNumber}>{pageNumber}</CurrPageButton>
            ) : (
              <PageButton
                key={pageNumber}
                onClick={() => setPage(pageNumber)}
              >
                {pageNumber}
              </PageButton>
            )
          ))}
          {currentPageWindow < totalPageWindows && (
            <PageWindowButton onClick={() => handlePageWindowClick('next')}>
              →
            </PageWindowButton>
          )}
        </PageButtonContainer>
        <PageSizeStationWrapper>
          <PageSizeStation
            currentValue={pageSize}
            handlePerPageChange={handlePageSizeChange}
            direction
          />
        </PageSizeStationWrapper>
      </Pagination>
    );
  };

  const body = (
    <ModalBody $isMobile={isMobile}>
      <Header $isMobile={isMobile}>
        <TitleGroup>
          <Title $isMobile={isMobile}>{getPageMessage('WORK_COPY_IMPORT_TITLE')}</Title>
          <SelectedCount>
            {getPageMessage('WORK_COPY_IMPORT_SELECTED', { count: selectedWorks.size })}
          </SelectedCount>
        </TitleGroup>
        <UniversalIconBtn
          size={isMobile ? 'fit' : 'small'}
          border={false}
          isTransparent
          hoverOpacity={!isMobile}
          backgroundImg={isMobile ? belowIcon : deleteIcon}
          onClick={close}
          aria-label="Close"
        />
      </Header>
      <Explain>{getPageMessage('WORK_COPY_IMPORT_EXPLAIN')}</Explain>

      {sourceStatus === 'idle' || sourceStatus === 'loading' ? (
        <StatusArea>{getPageMessage('WORK_COPY_IMPORT_SOURCE_LOADING')}</StatusArea>
      ) : sourceStatus === 'error' ? (
        <StatusArea role="alert">
          <span>{getPageMessage('WORK_COPY_IMPORT_SOURCE_FAILED')}</span>
          <RetryButton onClick={() => setSourceReloadKey((current) => current + 1)}>
            {getBtnName('RETRY')}
          </RetryButton>
        </StatusArea>
      ) : sources.length === 0 || selectedSourceId === null ? (
        <StatusArea>{getPageMessage('WORK_COPY_IMPORT_SOURCE_EMPTY')}</StatusArea>
      ) : (
        <ImportContent>
          <AccountField>
            <FieldLabel>{getPageMessage('WORK_COPY_IMPORT_ACCOUNT_LABEL')}</FieldLabel>
            <AccountSelectBoxWrapper>
              <SelectBox
                value={selectedSourceId}
                options={sources.map((source) => source.groupMemberId)}
                handleChange={handleSourceChange}
                direction={false}
                getDisplayText={getSourceDisplayText}
              />
            </AccountSelectBoxWrapper>
          </AccountField>
          {isMobile ? (
            <>
              <MobileSelectedContainer>
                {renderSelectedWorks('strip')}
              </MobileSelectedContainer>
              {renderLabels()}
              <CandidateArea>{renderCandidates()}</CandidateArea>
              {renderPagination()}
            </>
          ) : (
            <DesktopWorkGrid>
              <LabelSlot>{renderLabels()}</LabelSlot>
              <CandidateArea>{renderCandidates()}</CandidateArea>
              <PaginationSlot>{renderPagination()}</PaginationSlot>
              <DesktopSelectedContainer $isEmpty={selectedWorks.size === 0}>
                {selectedWorks.size === 0 ? (
                  <SelectedWorkPlaceholder>
                    {getPageMessage('WORK_COPY_IMPORT_SELECT_EMPTY')}
                  </SelectedWorkPlaceholder>
                ) : renderSelectedWorks('stack')}
              </DesktopSelectedContainer>
            </DesktopWorkGrid>
          )}
          <RequestButton onClick={() => void handleSubmit()}>
            {getPageMessage(
              isSubmitting ? 'WORK_COPY_IMPORT_REQUESTING' : 'WORK_COPY_IMPORT_REQUEST',
            )}
          </RequestButton>
        </ImportContent>
      )}
    </ModalBody>
  );

  if (isMobile) return <MobileFrame>{body}</MobileFrame>;
  return (
    <ImportModalFrame $isClosing={isClosing} onClick={(event) => event.stopPropagation()}>
      {body}
    </ImportModalFrame>
  );
};

const ImportModalFrame = styled(ChangeModalFrame)`
  width: min(820px, calc(100vw - 80px));
  height: min(760px, 88vh);
  box-sizing: border-box;
  overflow: hidden;
`;

const MobileFrame = styled.div`
  width: 100%;
  height: min(760px, calc(100dvh - 40px));
  min-height: 0;
  padding-bottom: 20px;
  box-sizing: border-box;
  overflow: hidden;
`;

const ModalBody = styled.div<{ $isMobile: boolean }>`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: ${({ $isMobile }) => ($isMobile ? '0' : '0.5vw')};
  box-sizing: border-box;
`;

const Header = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: ${({ $isMobile }) => ($isMobile ? 'clamp(12px, 2vh, 30px)' : '0')};
  margin-bottom: ${({ $isMobile }) => ($isMobile ? 'clamp(20px, 5vh, 30px)' : '3vh')};
`;

const Title = styled.h2<{ $isMobile: boolean }>`
  min-width: 0;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme, $isMobile }) => ($isMobile
    ? theme.typography.Body_01_1
    : theme.typography.Body_02_1)};
`;

const TitleGroup = styled.div`
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 12px;
`;

const Explain = styled.p`
  flex-shrink: 0;
  margin-bottom: 20px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  white-space: pre-line;
  ${({ theme }) => theme.typography.Body_04};
`;

const ImportContent = styled.div`
  min-height: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const AccountField = styled.div`
  flex-shrink: 0;
  margin-bottom: 18px;
`;

const AccountSelectBoxWrapper = styled.div`
  width: clamp(220px, 32vw, 380px);
  max-width: 100%;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_04};
`;

const FieldLabel = styled.div`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const DesktopWorkGrid = styled.div`
  min-height: 0;
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1fr) 200px;
  grid-template-rows: auto minmax(0, 1fr) auto;
  column-gap: 3vh;
`;

const LabelSlot = styled.div`
  grid-column: 1;
  grid-row: 1;
`;

const LabelContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const LabelButton = styled.button<{ $isActive: boolean }>`
  width: fit-content;
  padding: 0;
  border: 0;
  border-bottom: 1px dotted
    ${({ $isActive, theme }) => (
    $isActive ? theme.colors.color_Gray_03 : 'transparent'
  )};
  background: transparent;
  color: ${({ $isActive, theme }) => (
    $isActive ? theme.colors.color_Gray_03 : theme.colors.color_Gray_04
  )};
  cursor: pointer;
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.5)};
  transition: all 0.3s ease-in-out;
  ${({ theme }) => theme.typography.Body_04};

  &:hover {
    transform: scale(0.95);
  }
`;

const CandidateArea = styled.div`
  grid-column: 1;
  grid-row: 2;
  min-height: 0;
  display: flex;
  flex: 1;
  overflow: hidden;
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
`;

const CandidateList = styled.div<{ $marqueeSelecting: boolean }>`
  width: 100%;
  position: relative;
  overflow-y: auto;
  user-select: ${({ $marqueeSelecting }) => ($marqueeSelecting ? 'none' : 'auto')};

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CandidateRow = styled.div<{
  $disabled: boolean;
  $selected: boolean;
  $isMarqueeHit: boolean;
}>`
  width: 100%;
  height: clamp(56px, 7vh, 70px);
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  box-shadow: ${({ theme, $isMarqueeHit }) => (
    $isMarqueeHit ? `inset 0 0 0 1px ${theme.colors.color_Gray_05}` : 'none'
  )};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled, $selected }) => ($disabled ? 0.4 : $selected ? 0.5 : 1)};
  transition: opacity 0.2s ease-in-out;
`;

const ThumbnailFrame = styled.div`
  width: min(60%, 54px);
  height: calc(100% - 14px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const CandidateImageBlock = styled.div`
  height: 100%;
  min-width: 54px;
  display: flex;
  flex: 0.8;
  align-items: center;
  justify-content: center;
`;

const CandidateColumn = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0 4px;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  white-space: nowrap;
  text-overflow: ellipsis;
  ${({ theme }) => theme.typography.Body_04};
`;

const CandidateTitle = styled(CandidateColumn)`
  flex: 2;
  justify-content: center;
`;

const CandidateYear = styled(CandidateColumn)`
  flex: 0.6;
  justify-content: center;
`;

const CandidateMaterial = styled(CandidateColumn)`
  flex: 1.2;
  justify-content: center;
`;

const CandidateSize = styled(CandidateColumn)`
  flex: 1;
  justify-content: center;
`;

const DesktopSelectedContainer = styled.div<{ $isEmpty: boolean }>`
  grid-column: 2;
  grid-row: 2;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: ${({ $isEmpty }) => ($isEmpty ? 'center' : 'flex-start')};
  gap: 8px;
  overflow-y: auto;
  border-top: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};

  &::-webkit-scrollbar {
    display: none;
  }
`;

const SelectedWorkPlaceholder = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  text-align: center;
  white-space: pre-line;
  ${({ theme }) => theme.typography.Body_03_2};
`;

const MobileSelectedContainer = styled.div`
  height: 12vh;
  min-height: 90px;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  padding: 10px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};

  &::-webkit-scrollbar {
    display: none;
  }
`;

const SelectedThumbWrapper = styled.div<{ $layout: 'strip' | 'stack' }>`
  flex-shrink: 0;
  ${({ $layout }) => ($layout === 'stack'
    ? `
      width: 100%;
      display: flex;
      justify-content: flex-start;
    `
    : `
      height: 100%;
    `)}
`;

const SelectedImageBox = styled.div<{ $layout: 'strip' | 'stack' }>`
  position: relative;
  ${({ $layout }) => ($layout === 'stack'
    ? `
      max-width: 100%;
    `
    : `
      height: 100%;
    `)}
`;

const SelectedThumbImage = styled.img<{
  $isHover: boolean;
  $layout: 'strip' | 'stack';
}>`
  display: block;
  object-fit: contain;
  ${({ $layout }) => ($layout === 'stack'
    ? `
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 200px;
    `
    : `
      width: auto;
      height: 100%;
    `)}
  filter: ${({ $isHover }) => ($isHover ? 'blur(2px)' : 'none')};
  transition: filter 0.2s ease;
`;

const SelectedRemoveButton = styled(UniversalIconBtn)<{
  $isHover: boolean;
  $layout: 'strip' | 'stack';
}>`
  position: absolute;
  top: ${({ $layout }) => ($layout === 'strip' ? '2px' : '50%')};
  right: ${({ $layout }) => ($layout === 'strip' ? '2px' : 'auto')};
  left: ${({ $layout }) => ($layout === 'stack' ? '50%' : 'auto')};
  opacity: ${({ $isHover, $layout }) => ($layout === 'strip' || $isHover ? 1 : 0)};
  transform: ${({ $layout }) => ($layout === 'stack' ? 'translate(-50%, -50%)' : 'none')};
  transition: opacity 0.2s ease;

  &:focus-visible {
    opacity: 1;
  }

  &:not(:disabled):hover {
    transform: ${({ $layout }) => (
    $layout === 'stack' ? 'translate(-50%, -50%) scale(0.92)' : 'scale(0.92)'
  )};
  }
`;

const MarqueeSelectionBox = styled.div`
  position: absolute;
  z-index: 2;
  box-sizing: border-box;
  pointer-events: none;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: transparent;
  backdrop-filter: blur(1px);
`;

const StatusArea = styled.div`
  min-height: 160px;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  text-align: center;
  white-space: pre-line;
  ${({ theme }) => theme.typography.Body_04};
`;

const Pagination = styled.nav`
  width: 100%;
  min-height: 18px;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 30px;
  padding-top: 10px;
  overflow: visible;
  ${({ theme }) => theme.typography.Body_04};
`;

const PageButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PageSizeStationWrapper = styled.div`
  position: relative;
  z-index: 3;
  overflow: visible;
`;

const PaginationSlot = styled.div`
  grid-column: 1;
  grid-row: 3;
  overflow: visible;
`;

const RequestButton = styled(BlackFullWidthBtn)`
  width: 100%;
  flex-shrink: 0;
  margin-top: 14px;
`;

const SelectedCount = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const RetryButton = styled(PaperBlurFitBtn)`
  padding: 8px 1rem;
`;

export default WorkCopyImportModal;
